import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft, Trash2, RotateCcw, Loader2, AlertTriangle, Clock, Users,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRecycleBinPage = () => {
  const { token, isMasterAdmin } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/settings');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/crm/deleted-customers`, { headers });
      setCustomers(res.data.customers || []);
      if (res.data.purged_on_access?.length) {
        toast.info(`${res.data.purged_on_access.length} record(s) past their 30-day recovery window were permanently purged.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load recycle bin');
      if (err.response?.status === 403) navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [headers, navigate]);

  useEffect(() => {
    if (!isMasterAdmin) {
      toast.error('Recycle Bin requires master admin access');
      navigate('/admin');
      return;
    }
    fetchDeleted();
  }, [isMasterAdmin, fetchDeleted, navigate]);

  const restore = async (c) => {
    setBusyId(c.id);
    try {
      await axios.post(`${API_URL}/api/admin/crm/deleted-customers/${c.id}/restore`, {}, { headers });
      toast.success(`Restored ${c.full_name || c.email}`);
      fetchDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const purge = async () => {
    if (!purgeTarget) return;
    setBusyId(purgeTarget.id);
    try {
      await axios.post(`${API_URL}/api/admin/crm/deleted-customers/${purgeTarget.id}/purge`, {}, { headers });
      toast.success(`Permanently purged ${purgeTarget.full_name || purgeTarget.email}`);
      setPurgeTarget(null);
      fetchDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setBusyId(null);
    }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 flex items-center h-16 gap-2 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={back} data-testid="recycle-back-btn" className="flex-shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-heading text-zinc-900 truncate flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" /> Recycle Bin
            </h1>
            <p className="text-[11px] sm:text-xs text-zinc-500">Deleted customers · auto-purge after 30 days</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Info banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs sm:text-sm text-amber-900">
            <p className="font-semibold mb-0.5">30-day recovery window</p>
            <p>Records in the Recycle Bin are hidden from every list, dashboard, calendar, and search. Restore them at any time before their expiry, or purge them immediately for permanent deletion.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : customers.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <Users className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">Recycle bin is empty.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="recycle-bin-list">
            {customers.map(c => {
              const days = daysLeft(c.recoverable_until);
              const urgency = days <= 3 ? 'text-red-600' : days <= 7 ? 'text-amber-600' : 'text-zinc-600';
              return (
                <Card key={c.id} className="border-0 shadow-sm" data-testid={`recycle-row-${c.id}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-zinc-900 truncate">{c.full_name || 'Unnamed'}</p>
                          <Badge variant="outline" className={`text-[10px] ${urgency}`}>
                            <Clock className="w-3 h-3 mr-1" />
                            {days} day{days === 1 ? '' : 's'} left
                          </Badge>
                        </div>
                        <p className="text-xs text-zinc-500 truncate">{c.email}</p>
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500 flex-wrap">
                          {c.related_counts?.invoices > 0 && <span>{c.related_counts.invoices} invoice(s)</span>}
                          {c.related_counts?.appointments > 0 && <span>· {c.related_counts.appointments} appointment(s)</span>}
                          {c.related_counts?.notes > 0 && <span>· {c.related_counts.notes} note(s)</span>}
                          {c.related_counts?.addresses > 0 && <span>· {c.related_counts.addresses} address(es)</span>}
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 text-[11px] text-zinc-500" data-testid={`recycle-deleted-by-${c.id}`}>
                          <span className="font-semibold text-zinc-600">Deleted by</span>
                          <span className="text-zinc-800">{c.deleted_by_name || c.deleted_by_email || 'Unknown admin'}</span>
                          {c.deleted_at && (
                            <span className="text-zinc-400">· {new Date(c.deleted_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          )}
                        </div>
                        {c.deleted_reason && (
                          <p className="mt-2 text-[11px] italic text-zinc-500 border-l-2 border-zinc-200 pl-2">
                            Reason: {c.deleted_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restore(c)}
                          disabled={busyId === c.id}
                          data-testid={`recycle-restore-${c.id}`}
                        >
                          {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPurgeTarget(c)}
                          disabled={busyId === c.id}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`recycle-purge-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Purge
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently purge {purgeTarget?.full_name || 'this customer'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The customer plus every associated invoice, appointment, note, photo, and address will be permanently deleted from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyId === purgeTarget?.id}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={(e) => { e.preventDefault(); purge(); }}
              disabled={busyId === purgeTarget?.id}
              data-testid="confirm-purge-btn"
            >
              {busyId === purgeTarget?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Permanently Purge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRecycleBinPage;
